/**
 * Product Tabs controller.
 *
 * Provides responsive tab/accordion state, lazy hydration, deep-link
 * activation, and Shopify Theme Editor lifecycle support.
 */
(function() {
  const MANAGER_KEY = 'DrhProductTabs';
  const ROOT_SELECTOR = '.product-tabs';
  const reducedMotionQuery = typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  function rootsIn(scope) {
    if (!scope || typeof scope.querySelectorAll !== 'function') return [];

    const roots = [];
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(root => roots.push(root));
    return roots;
  }

  function createController(section) {
    if (!section || !section.matches(ROOT_SELECTOR)) return null;
    if (section.__productTabsController) return section.__productTabsController;
    section.__productTabsController = { destroy() {} };

    // --- Icon SVGs ---
    const icons = {
      'plus': '<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.16699 0C7.44299 0.000175763 7.66699 0.223966 7.66699 0.5V6.66699H13.833C14.1091 6.66699 14.333 6.89085 14.333 7.16699C14.3328 7.44298 14.109 7.66699 13.833 7.66699H7.66699V13.833C7.66699 14.109 7.44299 14.3328 7.16699 14.333C6.89085 14.333 6.66699 14.1092 6.66699 13.833V7.66699H0.5C0.223966 7.66699 0.000176609 7.44298 0 7.16699C1.20706e-08 6.89085 0.223857 6.66699 0.5 6.66699H6.66699V0.5C6.66699 0.223858 6.89085 0 7.16699 0Z" fill="currentColor"/></svg>',
      'minus': '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.666 9.5C16.9422 9.5 17.166 9.72386 17.166 10C17.166 10.2761 16.9422 10.5 16.666 10.5H3.33301C3.05687 10.5 2.83301 10.2761 2.83301 10C2.83301 9.72386 3.05687 9.5 3.33301 9.5H16.666Z" fill="currentColor"/></svg>',
      'info': '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>',
      'description': '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>',
      'composition': '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5M2 12l10 5 10-5"/></svg>',
      'feeding': '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>',
      'document': '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>',
      'list': '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>',
      'review': '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>',
      'question': '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>'
    };

    const sectionId = section.dataset.sectionId || section.id.replace(/^ProductTabs-/, '');
    const isDesignMode = section.dataset.designMode === 'true';
    const firstOpen = section.dataset.firstOpen === 'true';
    const renderMode = section.dataset.renderMode || 'eager';
    const showAll = renderMode === 'show_all';
    const tabList = section.querySelector('.product-tabs__tablist');
    const panelsContainer = section.querySelector('.product-tabs__panels');
    const panels = Array.from(section.querySelectorAll('.product-tabs__panel'));
    const headers = Array.from(section.querySelectorAll('.product-tabs__accordion-header'));
    const contents = Array.from(section.querySelectorAll('.product-tabs__content'));

    section.querySelectorAll('.product-tabs__accordion-icon-label[data-icon]').forEach(el => {
      const iconName = el.dataset.icon;
      if (icons[iconName]) el.innerHTML = icons[iconName];
    });

    // In design mode, @theme blocks may arrive after this script, so static
    // empty detection is intentionally skipped there.
    const visiblePanels = [];
    panels.forEach((panel, i) => {
      const header = headers[i];
      const content = contents[i];
      if (!header || !content) return;

      if (!isDesignMode) {
        const text = content.textContent?.trim() || '';
        const hasMedia = content.querySelector('img, svg, video, iframe, table, ul, ol');
        const hasLazyTemplate = content.querySelector('template[data-product-tabs-lazy-content]');
        if (!text && !hasMedia && !hasLazyTemplate) {
          panel.hidden = true;
          return;
        }
      }

      visiblePanels.push({ panel, header, content, index: i });
    });

    if (visiblePanels.length === 0) {
      delete section.__productTabsController;
      return;
    }

    function syncScrollableContent(vp) {
      const content = vp.content;
      const style = window.getComputedStyle(content);
      const canScrollX = style.overflowX === 'auto' || style.overflowX === 'scroll';
      const canScrollY = style.overflowY === 'auto' || style.overflowY === 'scroll';
      const hasOverflow = !content.hidden && (
        (canScrollX && content.scrollWidth > content.clientWidth + 1) ||
        (canScrollY && content.scrollHeight > content.clientHeight + 1)
      );
      if (hasOverflow) {
        content.setAttribute('tabindex', '0');
        content.dataset.scrollFocusManaged = 'true';
      } else if (content.dataset.scrollFocusManaged === 'true') {
        content.removeAttribute('tabindex');
        delete content.dataset.scrollFocusManaged;
      }
    }

    function syncAllScrollableContent() {
      visiblePanels.forEach(syncScrollableContent);
    }

    // Liquid emits these IDs. The fallback keeps editor-injected test markup safe.
    visiblePanels.forEach((vp, vi) => {
      if (!vp.header.id) vp.header.id = section.id + '-tab-' + (vi + 1);
      if (!vp.content.id) vp.content.id = section.id + '-panel-' + (vi + 1);
      if (!showAll) vp.header.setAttribute('aria-controls', vp.content.id);
      vp.content.setAttribute('aria-labelledby', vp.header.id);
    });

    let activeIndex = firstOpen ? 0 : -1;
    let isDesktop = null;
    const mobileOpenIndexes = new Set();

    visiblePanels.forEach((vp, vi) => {
      if (vp.header.getAttribute('aria-expanded') === 'true') mobileOpenIndexes.add(vi);
    });
    const initialActive = visiblePanels.findIndex(vp =>
      vp.header.classList.contains('product-tabs__accordion-header--active')
    );
    if (initialActive >= 0) activeIndex = initialActive;
    if (firstOpen && mobileOpenIndexes.size === 0) {
      mobileOpenIndexes.add(0);
      activeIndex = 0;
    }

    function hydrateLazyContent(vp) {
      const template = vp.content.querySelector('template[data-product-tabs-lazy-content]');
      if (!template) return;

      const fragment = template.content.cloneNode(true);
      fragment.querySelectorAll('script').forEach(script => {
        const replacement = document.createElement('script');
        Array.from(script.attributes).forEach(attr => {
          replacement.setAttribute(attr.name, attr.value);
        });
        replacement.textContent = script.textContent;
        replacement.dataset.productTabsLazyExecuted = 'true';
        script.replaceWith(replacement);
      });

      vp.content.appendChild(fragment);
      template.remove();
      vp.content.removeAttribute('data-product-tabs-lazy-pending');
    }

    function normalizeAnchor(value) {
      if (!value) return '';
      try {
        value = decodeURIComponent(value);
      } catch(e) {}
      return value
        .replace(/^#/, '')
        .trim()
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    function panelAnchor(vp) {
      return vp.content.dataset.anchorId || vp.content.dataset.tabAnchor || vp.header.dataset.tabAnchor || vp.panel.dataset.tabAnchor || '';
    }

    function findTabByAnchor(anchor) {
      const normalized = normalizeAnchor(anchor);
      if (!normalized) return -1;
      return visiblePanels.findIndex(vp =>
        normalizeAnchor(panelAnchor(vp)) === normalized || normalizeAnchor(vp.header.textContent) === normalized
      );
    }

    function scrollToTab(vp) {
      vp.header.scrollIntoView({
        behavior: reducedMotionQuery && reducedMotionQuery.matches ? 'instant' : 'smooth',
        block: 'start'
      });
    }

    function restoreHeadersToPanels() {
      const focusedHeader = visiblePanels.find(vp => vp.header === document.activeElement)?.header;
      visiblePanels.forEach(vp => {
        if (vp.header.parentElement !== vp.panel) {
          vp.panel.insertBefore(vp.header, vp.content);
        }
      });
      if (focusedHeader) focusedHeader.focus({ preventScroll: true });
    }

    function moveHeadersToTabList() {
      if (!tabList) return;
      const focusedHeader = visiblePanels.find(vp => vp.header === document.activeElement)?.header;
      visiblePanels.forEach(vp => tabList.appendChild(vp.header));
      if (focusedHeader) focusedHeader.focus({ preventScroll: true });
    }

    function setMobilePanelState(vp, open) {
      vp.panel.removeAttribute('role');
      vp.panel.hidden = false;
      vp.panel.classList.toggle('product-tabs__panel--active', open);
      vp.header.removeAttribute('role');
      vp.header.removeAttribute('aria-selected');
      vp.header.removeAttribute('tabindex');
      vp.header.setAttribute('aria-controls', vp.content.id);
      vp.header.setAttribute('aria-expanded', String(open));
      vp.content.setAttribute('role', 'region');
      vp.content.setAttribute('aria-labelledby', vp.header.id);
      vp.content.hidden = !open;
      vp.content.style.removeProperty('display');

      const icon = vp.header.querySelector('.product-tabs__accordion-icon');
      if (icon) {
        icon.style.removeProperty('display');
        icon.innerHTML = open ? icons.minus : icons.plus;
      }
      if (open) hydrateLazyContent(vp);
      syncScrollableContent(vp);
    }

    function enterMobile(mapDesktopSelection) {
      if (mapDesktopSelection) {
        mobileOpenIndexes.clear();
        if (activeIndex >= 0) mobileOpenIndexes.add(activeIndex);
      }

      isDesktop = false;
      section.classList.remove('product-tabs--desktop');
      restoreHeadersToPanels();
      if (tabList) tabList.removeAttribute('role');
      panelsContainer.removeAttribute('role');
      panelsContainer.removeAttribute('aria-owns');
      visiblePanels.forEach((vp, vi) => setMobilePanelState(vp, mobileOpenIndexes.has(vi)));
    }

    function activateTab(newIndex) {
      if (newIndex < 0 || newIndex >= visiblePanels.length) return;

      const next = visiblePanels[newIndex];
      const focusedPanel = visiblePanels.find(vp => vp.content.contains(document.activeElement));
      if (focusedPanel && focusedPanel !== next) next.header.focus();
      hydrateLazyContent(next);

      visiblePanels.forEach((vp, vi) => {
        const selected = vi === newIndex;
        vp.panel.hidden = !selected;
        vp.panel.classList.toggle('product-tabs__panel--active', selected);
        vp.header.classList.toggle('product-tabs__accordion-header--active', selected);
        vp.header.setAttribute('aria-selected', String(selected));
        vp.header.setAttribute('tabindex', selected ? '0' : '-1');
        vp.content.hidden = !selected;
        vp.content.style.removeProperty('display');
      });
      activeIndex = newIndex;
      syncAllScrollableContent();
    }

    function enterDesktop() {
      if (activeIndex < 0 || !mobileOpenIndexes.has(activeIndex)) {
        const openIndexes = Array.from(mobileOpenIndexes);
        activeIndex = openIndexes.length > 0 ? openIndexes[openIndexes.length - 1] : 0;
      }

      isDesktop = true;
      section.classList.add('product-tabs--desktop');
      panelsContainer.removeAttribute('role');
      panelsContainer.removeAttribute('aria-owns');
      if (tabList) tabList.setAttribute('role', 'tablist');
      moveHeadersToTabList();
      visiblePanels.forEach(vp => {
        vp.panel.removeAttribute('role');
        vp.header.setAttribute('role', 'tab');
        vp.header.setAttribute('aria-controls', vp.content.id);
        vp.header.removeAttribute('aria-expanded');
        vp.content.setAttribute('role', 'tabpanel');
        vp.content.setAttribute('aria-labelledby', vp.header.id);
        const icon = vp.header.querySelector('.product-tabs__accordion-icon');
        if (icon) icon.style.display = 'none';
      });

      activateTab(activeIndex);
      if (tabList) tabList.style.setProperty('--tab-count', String(visiblePanels.length));
      const headerHeight = visiblePanels[0]?.header.offsetHeight || 56;
      section.style.setProperty('--tab-bar-height', headerHeight + 'px');
    }

    function enterShowAll() {
      isDesktop = false;
      section.classList.remove('product-tabs--desktop');
      restoreHeadersToPanels();
      if (tabList) tabList.removeAttribute('role');
      panelsContainer.removeAttribute('role');
      panelsContainer.removeAttribute('aria-owns');
      visiblePanels.forEach(vp => {
        vp.panel.hidden = false;
        vp.panel.removeAttribute('role');
        vp.header.removeAttribute('role');
        vp.header.removeAttribute('aria-selected');
        vp.header.removeAttribute('aria-controls');
        vp.header.removeAttribute('aria-expanded');
        vp.header.removeAttribute('tabindex');
        vp.content.setAttribute('role', 'region');
        vp.content.setAttribute('aria-labelledby', vp.header.id);
        hydrateLazyContent(vp);
        vp.content.hidden = false;
        vp.content.style.removeProperty('display');
      });
      syncAllScrollableContent();
    }

    function toggleAccordion(header) {
      const vi = visiblePanels.findIndex(vp => vp.header === header);
      if (vi < 0) return;
      const vp = visiblePanels[vi];
      const open = !mobileOpenIndexes.has(vi);
      if (open) {
        mobileOpenIndexes.add(vi);
        activeIndex = vi;
      } else {
        mobileOpenIndexes.delete(vi);
        if (activeIndex === vi) {
          const remaining = Array.from(mobileOpenIndexes);
          activeIndex = remaining.length > 0 ? remaining[remaining.length - 1] : -1;
        }
      }
      setMobilePanelState(vp, open);
    }

    function handleTabKeydown(e) {
      if (!isDesktop || e.currentTarget.getAttribute('role') !== 'tab') return;
      const currentIndex = visiblePanels.findIndex(vp => vp.header === e.currentTarget);
      let newIndex = currentIndex;
      switch (e.key) {
        case 'ArrowRight':
          newIndex = (currentIndex + 1) % visiblePanels.length;
          break;
        case 'ArrowLeft':
          newIndex = (currentIndex - 1 + visiblePanels.length) % visiblePanels.length;
          break;
        case 'Home':
          newIndex = 0;
          break;
        case 'End':
          newIndex = visiblePanels.length - 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      activateTab(newIndex);
      visiblePanels[newIndex].header.focus();
    }

    function activateIndex(nextIndex, options) {
      if (nextIndex < 0 || nextIndex >= visiblePanels.length) return false;
      const vp = visiblePanels[nextIndex];
      if (showAll) {
        hydrateLazyContent(vp);
      } else if (isDesktop) {
        activateTab(nextIndex);
      } else {
        mobileOpenIndexes.add(nextIndex);
        activeIndex = nextIndex;
        setMobilePanelState(vp, true);
      }
      if (options && options.focus) {
        if (showAll) vp.header.setAttribute('tabindex', '-1');
        vp.header.focus({ preventScroll: true });
      }
      if (options && options.scroll) scrollToTab(vp);
      return true;
    }

    function activateAnchor(anchor, options) {
      return activateIndex(findTabByAnchor(anchor), options);
    }

    function writeHashForTab(vp) {
      const anchor = panelAnchor(vp);
      if (!anchor || window.location.hash === '#' + anchor) return;
      history.replaceState(null, '', '#' + anchor);
    }

    function handleAnchorClick(e) {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;
      if (!section.contains(link) && !(link.hasAttribute('data-product-description-link') && sectionOwnsGlobalAnchor(link.hash))) return;
      const anchor = link.getAttribute('href');
      if (findTabByAnchor(anchor) < 0) return;
      e.preventDefault();
      if (window.location.hash !== anchor) history.pushState(null, '', anchor);
      activateAnchor(anchor, { scroll: true, focus: true });
    }

    function handleClick(e) {
      if (showAll) return;
      const vi = visiblePanels.findIndex(vp => vp.header === e.currentTarget);
      if (vi < 0) return;
      if (isDesktop) {
        activateTab(vi);
        writeHashForTab(visiblePanels[vi]);
      } else {
        toggleAccordion(e.currentTarget);
        if (mobileOpenIndexes.has(vi)) writeHashForTab(visiblePanels[vi]);
      }
    }

    function sectionOwnsGlobalAnchor(anchor) {
      const roots = Array.from(document.querySelectorAll('.product-tabs'));
      const owner = roots.find(root => {
        const controller = root.__productTabsController;
        return typeof controller?.canActivateAnchor === 'function' && controller.canActivateAnchor(anchor);
      });
      return owner === section;
    }

    function handleHashChange() {
      if (sectionOwnsGlobalAnchor(window.location.hash)) {
        activateAnchor(window.location.hash, { scroll: true, focus: false });
      }
    }

    function handleExternalActivate(event) {
      const detail = event.detail || {};
      if (detail.sectionId && String(detail.sectionId) !== sectionId && String(detail.sectionId) !== section.id) return;
      if (!detail.sectionId) {
        const eventSection = event.target?.closest?.('.product-tabs');
        if (eventSection && eventSection !== section) return;
        if (!eventSection && !sectionOwnsGlobalAnchor(detail.anchor || detail.title)) return;
      }

      let nextIndex = -1;
      if (Number.isInteger(detail.index)) nextIndex = detail.index;
      if (nextIndex < 0 && (detail.anchor || detail.title)) {
        nextIndex = findTabByAnchor(detail.anchor || detail.title);
      }
      activateIndex(nextIndex, { scroll: detail.scroll !== false, focus: detail.focus !== false });
    }

    const mql = window.matchMedia('(min-width: 768px)');
    function handleBreakpoint(event) {
      if (showAll) {
        enterShowAll();
      } else if (event.matches) {
        enterDesktop();
      } else {
        enterMobile(isDesktop === true);
      }
    }

    visiblePanels.forEach(vp => {
      if (!showAll) vp.header.addEventListener('click', handleClick);
      vp.header.addEventListener('keydown', handleTabKeydown);
    });
    document.addEventListener('click', handleAnchorClick);
    document.addEventListener('drh:product-tabs:activate', handleExternalActivate);
    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('resize', syncAllScrollableContent);

    const contentResizeObserver = typeof window.ResizeObserver === 'function'
      ? new window.ResizeObserver(syncAllScrollableContent)
      : null;
    if (contentResizeObserver) {
      visiblePanels.forEach(vp => contentResizeObserver.observe(vp.content));
    }

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handleBreakpoint);
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(handleBreakpoint);
    }

    function destroy() {
      restoreHeadersToPanels();
      if (tabList) tabList.removeAttribute('role');
      visiblePanels.forEach(vp => {
        vp.header.removeEventListener('click', handleClick);
        vp.header.removeEventListener('keydown', handleTabKeydown);
      });
      document.removeEventListener('click', handleAnchorClick);
      document.removeEventListener('drh:product-tabs:activate', handleExternalActivate);
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('resize', syncAllScrollableContent);
      if (contentResizeObserver) contentResizeObserver.disconnect();
      visiblePanels.forEach(vp => {
        if (vp.content.dataset.scrollFocusManaged === 'true') {
          vp.content.removeAttribute('tabindex');
          delete vp.content.dataset.scrollFocusManaged;
        }
      });
      if (typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', handleBreakpoint);
      } else if (typeof mql.removeListener === 'function') {
        mql.removeListener(handleBreakpoint);
      }
      delete section.__productTabsController;
    }

    const controllerApi = {
      destroy,
      canActivateAnchor(anchor) {
        return findTabByAnchor(anchor) >= 0;
      },
    };
    section.__productTabsController = controllerApi;
    handleBreakpoint(mql);

    if (window.location.hash && sectionOwnsGlobalAnchor(window.location.hash)) {
      activateAnchor(window.location.hash, { scroll: true, focus: false });
    }
    return controllerApi;
  }

  function initScope(scope) {
    rootsIn(scope || document).forEach(root => createController(root));
  }

  function destroyScope(scope) {
    rootsIn(scope || document).forEach(root => {
      if (root.__productTabsController) root.__productTabsController.destroy();
    });
  }

  function eventScope(event) {
    if (event.target && event.target !== document) return event.target;
    const eventSectionId = event.detail && event.detail.sectionId;
    if (!eventSectionId) return event.target;
    return document.getElementById('ProductTabs-' + eventSectionId) || document.getElementById(eventSectionId);
  }

  if (window[MANAGER_KEY]) {
    window[MANAGER_KEY].initScope(document);
    return;
  }

  const manager = { initScope, destroyScope };
  window[MANAGER_KEY] = manager;
  document.addEventListener('shopify:section:load', event => manager.initScope(eventScope(event)));
  document.addEventListener('shopify:section:unload', event => manager.destroyScope(eventScope(event)));
  manager.initScope(document);
})();
